/**
 * Chat — paired chat thread between connected partners.
 *
 * Replaces the standalone Whispers feature with a casual, ongoing companion
 * to in-game play. Supports text bubbles, emoji reactions (toggle one slot
 * per user per message), reply-to-question-card context, and server-inserted
 * system messages ("Jamie completed a dare 🔥", "New game started", etc.).
 *
 * Routing:
 *   /chat       — this page
 *   /whispers   — legacy URL, redirects here
 *
 * Sync strategy: simple 5s polling for now (the messages collection is
 * lightweight and chat is opt-in). Can swap to SSE later via useRoomSync.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, X, Smile } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const REACTIONS = ["🔥", "💖", "😂", "👀", "💀", "🙌"];

export default function Chat() {
  const nav = useNavigate();
  const [thread, setThread] = useState({ items: [], unread_count: 0, partner: null });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [reactingTo, setReactingTo] = useState(null); // message id with picker open
  const [replyTo, setReplyTo] = useState(null);       // pending reply-to-card context
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.chatThread();
      setThread(r);
    } catch (e) {
      // 404 = no partner yet → leave empty state visible
      const status = e?.response?.status;
      if (status && status !== 404) toast.error("Couldn't load chat");
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // Mark all incoming messages as seen when the page is mounted + on every refresh
  useEffect(() => {
    api.chatSeen().catch(() => {});
  }, [thread.items.length]);

  // Scroll to bottom whenever new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread.items.length]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const newMsg = await api.chatSend({
        body,
        reply_to: replyTo,
      });
      setThread((prev) => ({ ...prev, items: [...prev.items, newMsg] }));
      setDraft("");
      setReplyTo(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't send";
      toast.error(typeof detail === "string" ? detail : "Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const handleReact = async (mid, emoji) => {
    setReactingTo(null);
    try {
      const updated = await api.chatReact(mid, emoji);
      setThread((prev) => ({
        ...prev,
        items: prev.items.map((m) => (m.id === mid ? updated : m)),
      }));
    } catch {
      toast.error("Couldn't react");
    }
  };

  const partner = thread.partner;

  return (
    <div className="vc-bg min-h-screen flex flex-col" style={{ background: "#0a060f" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-white/[0.08]"
        style={{ background: "rgba(15, 8, 22, 0.92)", backdropFilter: "blur(14px)" }}
      >
        <button
          onClick={() => nav(-1)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/5 transition"
          data-testid="chat-back"
          aria-label="back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-white font-extrabold text-base leading-tight truncate" data-testid="chat-partner-name">
            {partner ? partner.name : "Chat"}
          </div>
          <div className="text-[#9090A8] text-[11px] leading-tight">
            {partner ? "Connected • paired in your last game" : "Play a round together to start chatting 💕"}
          </div>
        </div>
      </header>

      {/* Empty state */}
      {!partner && (
        <div className="flex-1 flex items-center justify-center px-8 text-center text-[#9090A8]" data-testid="chat-empty">
          <div>
            <div className="text-5xl mb-4" aria-hidden>💬</div>
            <div className="text-white font-bold text-lg mb-1">No chat yet</div>
            <div className="text-sm">
              Start a round with your person and your chat appears here — react to dares, send reactions, keep the convo going between games.
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {partner && (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-3"
          data-testid="chat-thread"
        >
          {thread.items.length === 0 && (
            <div className="text-center text-[#9090A8] text-sm py-8" data-testid="chat-thread-empty">
              Say hi to {partner.name} 👋
            </div>
          )}
          <AnimatePresence initial={false}>
            {thread.items.map((m) => (
              <MessageBubble
                key={m.id}
                m={m}
                onLongPress={() => m.kind === "text" && setReactingTo(m.id)}
                isPickerOpen={reactingTo === m.id}
                onPickerClose={() => setReactingTo(null)}
                onReact={(emoji) => handleReact(m.id, emoji)}
              />
            ))}
          </AnimatePresence>
          <div ref={bottomRef} />
        </div>
      )}

      {/* Composer */}
      {partner && (
        <div
          className="sticky bottom-0 px-3 pt-2 pb-3 border-t border-white/[0.08]"
          style={{ background: "rgba(10, 6, 15, 0.95)", backdropFilter: "blur(16px)" }}
        >
          {replyTo && (
            <div
              className="rounded-xl px-3 py-2 mb-2 flex items-start gap-2"
              style={{ background: "rgba(255, 46, 154, 0.08)", border: "1px solid rgba(255, 46, 154, 0.3)" }}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-0.5" style={{ color: "#FF7BC0" }}>
                  Replying to question
                </div>
                <div className="text-white text-[13px] truncate">{replyTo.question}</div>
              </div>
              <button
                onClick={() => setReplyTo(null)}
                className="w-6 h-6 rounded-full flex items-center justify-center text-white/60 hover:text-white"
                data-testid="chat-clear-reply"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 1000))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="message…"
              rows={1}
              className="flex-1 resize-none bg-white/[0.06] text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm outline-none border border-white/[0.08] focus:border-[#FF2E9A]/50 transition"
              data-testid="chat-input"
              style={{ maxHeight: 140 }}
            />
            <button
              onClick={handleSend}
              disabled={!draft.trim() || sending}
              className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #FF2E9A 0%, #9B6BFF 100%)",
                boxShadow: "0 0 18px rgba(255, 46, 154, 0.55)",
              }}
              data-testid="chat-send"
              aria-label="send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * Individual message bubble — text or system.
 * ========================================================================= */
function MessageBubble({ m, onLongPress, isPickerOpen, onPickerClose, onReact }) {
  const longPressTimer = useRef(null);

  // System messages render center-aligned, no bubble
  if (m.kind === "system") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="self-center text-[11.5px] font-semibold tracking-wide rounded-full px-3 py-1.5 my-1"
        style={{ background: "rgba(155, 107, 255, 0.10)", color: "#C49BFF", border: "1px solid rgba(155, 107, 255, 0.25)" }}
        data-testid={`chat-system-${m.id}`}
      >
        {m.body}
      </motion.div>
    );
  }

  const isMine = m.is_sender;
  const bubbleSide = isMine ? "self-end" : "self-start";
  const bubbleStyle = isMine
    ? {
        background: "linear-gradient(135deg, #FF2E9A 0%, #C84BAF 100%)",
        color: "#fff",
        borderBottomRightRadius: 6,
        boxShadow: "0 0 18px rgba(255, 46, 154, 0.32)",
      }
    : {
        background: "rgba(255, 255, 255, 0.06)",
        color: "#fff",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderBottomLeftRadius: 6,
      };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22 }}
      className={`relative max-w-[78%] ${bubbleSide}`}
      data-testid={`chat-message-${m.id}`}
    >
      <div
        onPointerDown={() => { longPressTimer.current = setTimeout(onLongPress, 450); }}
        onPointerUp={() => clearTimeout(longPressTimer.current)}
        onPointerLeave={() => clearTimeout(longPressTimer.current)}
        onContextMenu={(e) => { e.preventDefault(); onLongPress?.(); }}
        className="rounded-2xl px-4 py-2.5 text-[14px] leading-snug whitespace-pre-wrap break-words"
        style={bubbleStyle}
      >
        {m.reply_to && (
          <div
            className="rounded-lg px-2.5 py-1.5 mb-1.5 text-[12px]"
            style={{
              background: isMine ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 46, 154, 0.12)",
              borderLeft: `2.5px solid ${isMine ? "rgba(255,255,255,0.55)" : "#FF2E9A"}`,
            }}
          >
            <div className="text-[9.5px] uppercase tracking-[0.2em] font-black opacity-80 mb-0.5">
              Replying to question
            </div>
            <div className="opacity-90 leading-tight">{m.reply_to.question}</div>
          </div>
        )}
        {m.body}
      </div>

      {/* Sender name + time */}
      <div className={`flex items-center gap-2 mt-1 px-1 text-[10.5px] text-[#9090A8] ${isMine ? "justify-end" : "justify-start"}`}>
        <span>{isMine ? "you" : m.sender_name}</span>
        <span aria-hidden>·</span>
        <span>{formatTime(m.created_at)}</span>
      </div>

      {/* Reactions row */}
      {(m.reactions || []).length > 0 && (
        <div className={`flex gap-1 mt-1 ${isMine ? "justify-end" : "justify-start"}`}>
          {groupReactions(m.reactions).map(({ emoji, count }) => (
            <button
              key={emoji}
              onClick={() => onReact(emoji)}
              className="rounded-full text-[12px] px-2 py-[2px] flex items-center gap-1"
              style={{ background: "rgba(255, 46, 154, 0.18)", border: "1px solid rgba(255, 46, 154, 0.4)" }}
              data-testid={`chat-reaction-${emoji}`}
            >
              <span>{emoji}</span>
              {count > 1 && <span className="text-white text-[10.5px] font-bold">{count}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Reaction picker — small floating popover on long-press */}
      <AnimatePresence>
        {isPickerOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.9 }}
            className={`absolute -top-12 z-20 rounded-full px-2 py-1.5 flex gap-1.5 ${isMine ? "right-0" : "left-0"}`}
            style={{
              background: "rgba(15, 8, 22, 0.98)",
              border: "1px solid rgba(255, 46, 154, 0.4)",
              boxShadow: "0 8px 22px rgba(0, 0, 0, 0.5), 0 0 18px rgba(255, 46, 154, 0.35)",
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`chat-reaction-picker-${m.id}`}
          >
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => onReact(emoji)}
                className="text-xl hover:scale-125 transition active:scale-95"
                aria-label={emoji}
                data-testid={`chat-react-${emoji}`}
              >
                {emoji}
              </button>
            ))}
            <button
              onClick={onPickerClose}
              className="text-white/50 px-1 hover:text-white"
              aria-label="close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/** Aggregate reactions into {emoji, count} pairs for compact display. */
function groupReactions(reactions) {
  const counts = {};
  for (const r of reactions || []) counts[r.emoji] = (counts[r.emoji] || 0) + 1;
  return Object.entries(counts).map(([emoji, count]) => ({ emoji, count }));
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}
